import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroBackgroundAnimation } from "./HeroBackgroundAnimation";
import { useLanguage } from "@/hooks/useLanguage";

export function HeroSection() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <section className="relative overflow-hidden bg-primary min-h-[320px] lg:min-h-[700px]">
      {/* Background animation - hidden on mobile for performance */}
      <div className="hidden lg:block">
        <HeroBackgroundAnimation />
      </div>

      {/* Gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/50 to-transparent" />

      {/* Stronger overlay on mobile */}
      <div className="absolute inset-0 bg-primary/40 lg:bg-transparent" />

      <div className="relative container mx-auto px-4 lg:px-8 py-10 lg:py-28 flex items-center min-h-[320px] lg:min-h-[700px]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl text-left"
        >
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-medium font-heading text-primary-foreground leading-tight tracking-tight">
            {t("hero.title")}
          </h1>
          <p className="mt-4 lg:mt-6 text-base md:text-xl text-primary-foreground/80 font-body leading-relaxed">
            {t("hero.subtitle")}
          </p>
          <div className="mt-6 lg:mt-8 flex flex-col sm:flex-row gap-4 justify-start">
            <Button
              size="lg"
              asChild
              className="bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8"
            >
              <Link to={getLocalizedPath("/kontakt?type=demo")}>
                {t("hero.cta")}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              asChild
              className="bg-white/15 border border-white/30 text-primary-foreground hover:bg-white/25 font-medium text-base backdrop-blur-sm"
            >
              <Link to={getLocalizedPath("/losningar")}>{t("hero.ctaSecondary")}</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
